'use client'

import { useFormatTimeSpan } from '../_hooks/use-format-time-span'
import { VerifiedPremiumRibbon } from '@web/app/[locale]/clubhouse/[slug]/_components/verified-premium-ribbon'
import type { SocialComment } from 'codegen/types'
import Image from 'next/image'
import * as React from 'react'
import { MarkdownViewer } from 'primitives/markdown-viewer'
import { formatUserName } from '@web/app/[locale]/(web)/masterfeed/utils/format-user-name'
import { IconAccountCircle, IconExpandMore } from '@repo/design-system-cf/components/icons'
import type { Project } from '../page-client'
import { sanitizeText } from '../utils/sanitize-text'
import { ProjectCommentInput } from './project-comment-input'
import { ProjectCommentButton } from './project-actions/project-comment-button'
import { ProjectCommentVoteButton } from './project-actions/project-comment-vote-button'
import { useComments } from '../_hooks/use-project-comments'
import { ProjectChildComment } from './project-child-comment'
import { DiscussionCommentMedia } from '@web/app/[locale]/clubhouse/[slug]/(social)/discussions/[topicSlug]/[discussionSlug]/_components/discussion-comment-media'
import { PostCommentAction } from './post-comment-action'
import { NEXT_PUBLIC_CF_URL } from '@web/utils/env'
import { Link } from '@repo/web-ui/components/link'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import { Button } from '@repo/design-system-cf/components/button'
import { usePhotoNavigation } from '../_hooks/use-photo-navigation'

export function ProjectComment(props: {
  comment: SocialComment
  projectData: Project
  onCommentUpdate?: () => void
  onBumpCommentsCount?: () => void
  onDecrementCommentsCount?: () => void
}) {
  return (
    <React.Suspense>
      <ProjectCommentInner {...props} />
    </React.Suspense>
  )
}

// oxlint-disable-next-line complexity
function ProjectCommentInner({
  comment,
  projectData,
  onCommentUpdate,
  onBumpCommentsCount,
  onDecrementCommentsCount,
}: {
  comment: SocialComment
  projectData: Project
  onCommentUpdate?: () => void
  onBumpCommentsCount?: () => void
  onDecrementCommentsCount?: () => void
}) {
  const formatTimeSpan = useFormatTimeSpan()
  const [replying, setReplying] = React.useState(false)
  const [focusCounter, setFocusCounter] = React.useState(0)
  const { breakpoint } = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')

  const firstAsset = comment.assets?.[0]
  const { prevQuery, photoHref, hideInlineImage, redirectUrlBase, openPhoto } = usePhotoNavigation({
    postSlug: projectData.postSlug,
    commentId: comment.id,
    firstAssetId: firstAsset?.id,
  })

  const timeElapsed = React.useMemo(
    () =>
      comment.createTime &&
      formatTimeSpan(new Date().getTime() - new Date(comment.createTime).getTime()),
    [comment.createTime, formatTimeSpan],
  )

  const {
    comments: repliesToComments,
    hasNextPage,
    handleLoadMore,
    handleNewComment,
    bumpCommentReplyCount,
    isFetchingNextPage,
  } = useComments(projectData.id || '', comment.id || '', (comment.commentsCount ?? 0) > 0)

  const handleReply = () => {
    setReplying(v => !v)
    setFocusCounter(prev => prev + 1)
  }

  const userFullName = comment.user
    ? formatUserName(comment?.user.firstName, comment?.user.lastName, 'User')
    : 'User'

  return (
    <>
      <div className="flex gap-x-1 py-2" data-testid="project-comment">
        <div className="flex-shrink-0">
          {comment.content?.toString().includes('This comment has been removed.') ? (
            <div className="flex items-center justify-center rounded-full bg-cf-grey-500 mt-[14px] size-8">
              <IconAccountCircle
                className="text-cf-white size-5"
                data-testid="project-comment-avatar"
              />
            </div>
          ) : (
            <div className="relative size-8">
              <Image
                src={comment.user.avatarUrl || ''}
                alt="avatar"
                width={32}
                height={32}
                data-testid="project-comment-avatar"
                className="rounded-full mt-[47px] tablet:mt-5 size-8"
              />
              <div className="absolute inset-0 rounded-full border border-[#1313151F]" />
            </div>
          )}
          {repliesToComments && repliesToComments.length > 0 ? (
            <div className="w-full h-[calc(100%-22px)] flex justify-center">
              <div className="bg-cf-grey-250 w-[2px] h-full" />
            </div>
          ) : null}
        </div>

        <div className="border bg-cf-grey-25 rounded-[12px] py-3 px-4 border-cf-grey-250 w-full relative">
          {isMobile ? (
            <div className="flex items-center justify-between mb-1">
              <p className="text-12 text-cf-grey-600 text-nowrap">{timeElapsed}</p>
              <PostCommentAction comment={comment} onDeleted={onDecrementCommentsCount} />
            </div>
          ) : null}
          <div className="flex justify-between items-center">
            <div>
              <div className="flex flex-row gap-0.5">
                <Link
                  target="_blank"
                  href={`${NEXT_PUBLIC_CF_URL}/crafter/${comment.user.slug}/`}
                  className="text-14 font-semibold text-cf-grey-1000 line-clamp-2"
                  data-testid="project-comment-username-link"
                >
                  {userFullName}
                </Link>
                <VerifiedPremiumRibbon user={comment.user} />
              </div>
              <p className="text-12 text-cf-grey-600">{comment.user.superSkill}</p>
            </div>
            {!isMobile ? (
              <div className="items-center justify-center gap-4 hidden tablet:flex">
                <p className="text-12 text-cf-grey-500 ">{timeElapsed}</p>

                <PostCommentAction comment={comment} onDeleted={onDecrementCommentsCount} />
              </div>
            ) : null}
          </div>
          <>
            <div
              style={{ overflowWrap: 'anywhere' }}
              className="mt-3 text-16 text-cf-grey-800 break-anywhere whitespace-normal"
              data-testid="project-comment-content"
            >
              <MarkdownViewer enableHttpsLinkDetection={true} redirectUrlBase={redirectUrlBase}>
                {sanitizeText(comment.content || '')}
              </MarkdownViewer>
            </div>
            <DiscussionCommentMedia
              assets={comment.assets}
              commentId={comment.id}
              hideInline={hideInlineImage}
              prevQuery={prevQuery}
              photoHref={photoHref}
              onOpenPhoto={openPhoto}
            />
            {/* comment reactions */}
            <div className="flex flex-row gap-2 mt-3">
              <ProjectCommentVoteButton project={projectData} size="small" comment={comment} />
              <ProjectCommentButton
                onClick={handleReply}
                size="small"
                commentCount={comment.commentsCount || 0}
              />
            </div>
            <div className="mt-3">
              <ProjectCommentInput
                open={replying}
                focus={focusCounter}
                projectData={projectData}
                parentComment={comment}
                onSubmit={newComment => {
                  handleNewComment(newComment)
                  bumpCommentReplyCount(comment.id)
                  onBumpCommentsCount?.()
                }}
                setOpen={setReplying}
              />
            </div>
          </>
        </div>
      </div>

      <div className="relative" data-testid="project-child-comments-container">
        {repliesToComments.map((reply, idx) => (
          <ProjectChildComment
            key={reply.id}
            comment={reply}
            level={1}
            parentComment={comment}
            project={projectData}
            numberOfReplies={repliesToComments.length}
            index={idx + 1}
            onCommentUpdate={onCommentUpdate}
            onParentUpdate={newComment => {
              handleNewComment(newComment)
              bumpCommentReplyCount(comment.id)
              onBumpCommentsCount?.()
            }}
            onBumpCommentsCount={onBumpCommentsCount}
            onDecrementCommentsCount={onDecrementCommentsCount}
            hasMore={hasNextPage}
          />
        ))}

        {hasNextPage ? (
          <div className="flex flex-row gap-x-1 mt-1 relative">
            <div className="absolute top-0 -translate-y-1 translate-x-[15px] w-[21px] sm:h-[58px] h-[28px] tablet:h-7 rounded-bl-2 border-l-2 border-b-2 border-cf-grey-250" />
            {/* one indent = level 1 */}
            <div className="w-[36px] flex-shrink-0" />
            <div className="flex-1">
              <Button
                variant="tertiary"
                size="small"
                className="mt-2 mb-4"
                onClick={handleLoadMore}
                loading={isFetchingNextPage}
                iconRight={<IconExpandMore />}
              >
                View more
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}
